import {
  FilterList as FilterListIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import {
  Box,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  Table,
  TableBody, TableCell, TableContainer,
  TableHead,
  TablePagination, TableRow, TextField,
  Toolbar,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useReferenceData } from "../../../hooks";
import { UploadLithologyLine } from "../../../types/interfaces/file/imports/lithologies";
import { UploadLithologyForm } from "./UploadLithologyForm";

interface RowProps {
  line: UploadLithologyLine;
  open: boolean;
  onToggle: () => void;
  lithologies: { id: number; name: string }[];
  secondaryFeatures: { id: number; name: string }[];
  textures: { id: number; name: string }[];
  colors: { id: number; name: string }[];
  aquiferSystems: { id: number; name: string }[];
}

const Row = ({ 
  line, 
  open, 
  onToggle, 
  lithologies, 
  secondaryFeatures, 
  textures, 
  colors, 
  aquiferSystems 
}: RowProps) => {
  const theme = useTheme();
  const preview = line.preview;
  if (!preview) return null;

  const lithologyName = 
    lithologies.find(m => m.id === preview.lithologyId)?.name
    ?? "";
  
  const secondaryFeatureName = 
    secondaryFeatures.find(m => m.id === preview.secondaryFeatureId)?.name
    ?? "";

  const textureName =
    textures.find(m => m.id === preview.textureId)?.name
    ?? "";
  
  const colorName =
    colors.find(m => m.id === preview.colorId)?.name
    ?? "";

  const aquiferName =
    aquiferSystems.find(m => m.id === preview.aquiferId)?.name
    ?? "";

  return (
    <React.Fragment>
      <TableRow
        onClick={onToggle}
        sx={{ cursor: "pointer", "& > *": { borderBottom: "unset" } }}
      >
        <TableCell>
          {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        </TableCell>
        <TableCell component="th" scope="row">
          {preview.startDepth}
        </TableCell>
        <TableCell>{preview.endDepth}</TableCell>
        <TableCell>{lithologyName}</TableCell>
        <TableCell>{aquiferName}</TableCell>
      </TableRow>
      <TableRow sx={open ? { backgroundColor: theme.palette.action.hover } : {}}>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <UploadLithologyForm 
                lithology={preview} 
                lithologyName={lithologyName} 
                secondaryFeatureName={secondaryFeatureName}
                textureName={textureName}
                colorName={colorName}
                aquiferName={aquiferName}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

interface UploadLithologiesPreviewTableProps {
  lines: UploadLithologyLine[];
}

export function UploadLithologiesPreviewTable({
  lines,
}: Readonly<UploadLithologiesPreviewTableProps>) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [filter, setFilter] = useState("");
  const [openRowId, setOpenRowId] = useState<number | null>(null);

  const { data: lithologies = [] } = useReferenceData("geology", "lithologies");
  const { data: secondaryFeatures = [] } = useReferenceData("geology", "secondary_features");
  const { data: textures = [] } = useReferenceData("geology", "textures");
  const { data: colors = [] } = useReferenceData("geology", "colors");
  const { data: aquiferSystems = [] } = useReferenceData("hydro", "aquifer_systems");

  
  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value);
    setPage(0);
  };

  const filteredData = lines.filter((line) => {
    const preview = line.preview;
    if (!preview) return false;

    const lithologyName =
      lithologies.find((m) => m.id === preview.lithologyId)?.name ?? "";

    const secondaryFeatureName =
      secondaryFeatures.find((m) => m.id === preview.secondaryFeatureId)?.name ?? "";

    const textureName =
      textures.find((m) => m.id === preview.textureId)?.name ?? "";

    const colorName =
      colors.find((m) => m.id === preview.colorId)?.name ?? "";

    const aquiferName =
      aquiferSystems.find((m) => m.id === preview.aquiferId)?.name ?? "";

    const search = filter.trim().toLowerCase();

    if (!search) return true;

    return [
      lithologyName,
      secondaryFeatureName,
      textureName,
      colorName,
      aquiferName,
      preview.startDepth,
      preview.endDepth,
      preview.hydraulicConductivity,
      preview.notes,
    ]
      .filter(Boolean)
      .some((value) =>
        String(value).toLowerCase().includes(search)
      );
  });

  const visibleRows = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
      <Paper sx={{ p: 2}}>
        <Toolbar>
          <Typography sx={{ flex: "1 1 100%" }} variant="h5">
            {t("imports.confirm-dialog.data-table-title")}
          </Typography>
          <Tooltip title={t("generic.filter")}>
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              <FilterListIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
  
        {showFilters && (
          <Grid2 container spacing={3} sx={{ p: 2 }}>
            <Grid2 size={12}>
              <TextField
                label={t("generic.search")}
                value={filter}
                onChange={handleFilterChange}
                fullWidth
              />
            </Grid2>
          </Grid2>
        )}
  
        <TableContainer>
          <Table aria-label="collapsible table" sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "5%" }} />
                <TableCell>{t("captures.capture.lithology.start-depth")}</TableCell>
                <TableCell>{t("captures.capture.lithology.end-depth")}</TableCell>
                <TableCell>{t("captures.capture.lithology.lithology-type")}</TableCell>
                <TableCell>{t("captures.capture.lithology.aquifer-system")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((line) => (
                <Row
                  key={line.line}
                  line={line}
                  open={openRowId === line.line}
                  onToggle={() =>
                    setOpenRowId(openRowId === line.line ? null : line.line)
                  }
                  lithologies={lithologies}
                  secondaryFeatures={secondaryFeatures}
                  textures={textures}
                  colors={colors}
                  aquiferSystems={aquiferSystems}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
  
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number.parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>
    );
};